import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { KIND_MANUAL_TAX_PROJECTION, looksLikeATaxLine } from '../utils/admissionTax';

const V3OfferContext = createContext();

export const useV3OfferContext = () => {
  return useContext(V3OfferContext);
};

export const V3_ACTIONS = {
  SET_OFFER: 'SET_OFFER',
  UPDATE_OFFER: 'UPDATE_OFFER',
  SET_BUDGET: 'SET_BUDGET',
  UPDATE_BUDGET: 'UPDATE_BUDGET',
  SET_LINE_ITEMS: 'SET_LINE_ITEMS',
  SET_EVENT_OFFERS: 'SET_EVENT_OFFERS',
  ADD_LINE_ITEM: 'ADD_LINE_ITEM',
  UPDATE_LINE_ITEM: 'UPDATE_LINE_ITEM',
  REMOVE_LINE_ITEM: 'REMOVE_LINE_ITEM',
  REORDER_LINE_ITEMS: 'REORDER_LINE_ITEMS',
  ADD_BUDGET_BONUS: 'ADD_BUDGET_BONUS',
  UPDATE_BUDGET_BONUS: 'UPDATE_BUDGET_BONUS',
  REMOVE_BUDGET_BONUS: 'REMOVE_BUDGET_BONUS',
  ADD_BUDGET_DEAL_BRACKET: 'ADD_BUDGET_DEAL_BRACKET',
  UPDATE_BUDGET_DEAL_BRACKET: 'UPDATE_BUDGET_DEAL_BRACKET',
  REMOVE_BUDGET_DEAL_BRACKET: 'REMOVE_BUDGET_DEAL_BRACKET',
  SET_BUDGET_DEAL_BRACKETS: 'SET_BUDGET_DEAL_BRACKETS',
  SET_DEAL_BRACKETS: 'SET_DEAL_BRACKETS',
  UPDATE_DEAL_BRACKET: 'UPDATE_DEAL_BRACKET',
  SET_SAVING: 'SET_SAVING',
  SET_SAVING_DEAL_BRACKET_BASIS: 'SET_SAVING_DEAL_BRACKET_BASIS',
  SET_LOADING: 'SET_LOADING',
  SET_MODAL_UI_STATE: 'SET_MODAL_UI_STATE',
  SET_BUDGET_TEMPLATE: 'SET_BUDGET_TEMPLATE',
  SET_IS_CREATING_TEMPLATE: 'SET_IS_CREATING_TEMPLATE',
  SET_OFFER_CONTACTS: 'SET_OFFER_CONTACTS',
  TEMPLATE_APPLIED: 'TEMPLATE_APPLIED',
  SET_SAVE_AS_NEW_OPEN: 'SET_SAVE_AS_NEW_OPEN',
  SET_SAVE_AS_NEW_DRAFT: 'SET_SAVE_AS_NEW_DRAFT',
};

const initialState = {
  offer: null,
  budget: null,
  lineItems: [],
  dealBrackets: [],
  // Sibling V3Offers (other offers on the same calendar_event, or for
  // pre-event drafts, other offers sharing the v3_budget). Seeded from the
  // SSR'd page prop and refreshed by SET_EVENT_OFFERS whenever the user
  // creates a new sibling or switches tabs (O-2993).
  eventOffers: [],
  isSaving: false,
  isSavingDealBracketBasis: false,
  isLoading: false,
  modalUIState: null,
  budgetTemplate: null,
  isCreatingTemplate: false,
  offerContacts: [],
  // Lifted out of useV3OfferTemplate's useState so the form's inline "Save as
  // new" link and OriginCard's chevron-menu item share the same modal state.
  // Each consumer of the hook had its own useState slice, so the inline link
  // toggled state nobody was rendering.
  saveAsNewModalOpen: false,
  saveAsNewModalDraft: '',
};

function v3OfferReducer(state, action) {
  switch (action.type) {
    case V3_ACTIONS.SET_OFFER:
      return { ...state, offer: action.payload };
    case V3_ACTIONS.UPDATE_OFFER:
      return { ...state, offer: { ...state.offer, ...action.payload } };
    case V3_ACTIONS.SET_BUDGET:
      return { ...state, budget: action.payload };
    case V3_ACTIONS.UPDATE_BUDGET:
      return { ...state, budget: { ...state.budget, ...action.payload } };
    case V3_ACTIONS.ADD_BUDGET_BONUS:
      return {
        ...state,
        budget: {
          ...state.budget,
          v3_budget_bonuses: [...(state.budget?.v3_budget_bonuses || []), action.payload],
        },
      };
    case V3_ACTIONS.UPDATE_BUDGET_BONUS:
      return {
        ...state,
        budget: {
          ...state.budget,
          v3_budget_bonuses: (state.budget?.v3_budget_bonuses || []).map((bonus) =>
            bonus.id === action.payload.id ? { ...bonus, ...action.payload } : bonus
          ),
        },
      };
    case V3_ACTIONS.REMOVE_BUDGET_BONUS:
      return {
        ...state,
        budget: {
          ...state.budget,
          v3_budget_bonuses: (state.budget?.v3_budget_bonuses || []).filter(
            (bonus) => bonus.id !== action.payload
          ),
        },
      };
    case V3_ACTIONS.ADD_BUDGET_DEAL_BRACKET:
      return {
        ...state,
        budget: {
          ...state.budget,
          v3_deal_brackets: [...(state.budget?.v3_deal_brackets || []), action.payload],
        },
      };
    case V3_ACTIONS.UPDATE_BUDGET_DEAL_BRACKET:
      return {
        ...state,
        budget: {
          ...state.budget,
          v3_deal_brackets: (state.budget?.v3_deal_brackets || []).map((bracket) =>
            bracket.id === action.payload.id ? { ...bracket, ...action.payload } : bracket
          ),
        },
      };
    case V3_ACTIONS.REMOVE_BUDGET_DEAL_BRACKET:
      return {
        ...state,
        budget: {
          ...state.budget,
          v3_deal_brackets: (state.budget?.v3_deal_brackets || []).filter(
            (bracket) => bracket.id !== action.payload
          ),
        },
      };
    case V3_ACTIONS.SET_BUDGET_DEAL_BRACKETS:
      return {
        ...state,
        budget: { ...state.budget, v3_deal_brackets: action.payload },
      };
    case V3_ACTIONS.SET_LINE_ITEMS:
      return { ...state, lineItems: action.payload };
    case V3_ACTIONS.SET_EVENT_OFFERS:
      return { ...state, eventOffers: action.payload || [] };
    case V3_ACTIONS.ADD_LINE_ITEM:
      return { ...state, lineItems: [...state.lineItems, action.payload] };
    case V3_ACTIONS.UPDATE_LINE_ITEM:
      return {
        ...state,
        lineItems: state.lineItems.map((item) =>
          item.id === action.payload.id ? { ...item, ...action.payload } : item
        ),
      };
    case V3_ACTIONS.REMOVE_LINE_ITEM:
      return {
        ...state,
        lineItems: state.lineItems.filter((item) => item.id !== action.payload),
      };
    case V3_ACTIONS.REORDER_LINE_ITEMS:
      return { ...state, lineItems: action.payload };
    case V3_ACTIONS.SET_DEAL_BRACKETS:
      return { ...state, dealBrackets: action.payload };
    case V3_ACTIONS.UPDATE_DEAL_BRACKET:
      return {
        ...state,
        dealBrackets: state.dealBrackets.map((bracket) =>
          bracket.id === action.payload.id ? { ...bracket, ...action.payload } : bracket
        ),
      };
    case V3_ACTIONS.SET_SAVING:
      return { ...state, isSaving: action.payload };
    case V3_ACTIONS.SET_SAVING_DEAL_BRACKET_BASIS:
      return { ...state, isSavingDealBracketBasis: action.payload };
    case V3_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case V3_ACTIONS.SET_MODAL_UI_STATE:
      return { ...state, modalUIState: action.payload };
    case V3_ACTIONS.SET_BUDGET_TEMPLATE:
      return { ...state, budgetTemplate: action.payload };
    case V3_ACTIONS.SET_IS_CREATING_TEMPLATE:
      return { ...state, isCreatingTemplate: action.payload };
    case V3_ACTIONS.SET_OFFER_CONTACTS:
      return { ...state, offerContacts: action.payload };
    case V3_ACTIONS.TEMPLATE_APPLIED:
      return {
        ...state,
        budget: action.payload.budget,
        lineItems: action.payload.lineItems || [],
        dealBrackets: action.payload.dealBrackets || [],
      };
    case V3_ACTIONS.SET_SAVE_AS_NEW_OPEN:
      return { ...state, saveAsNewModalOpen: !!action.payload };
    case V3_ACTIONS.SET_SAVE_AS_NEW_DRAFT:
      return { ...state, saveAsNewModalDraft: action.payload ?? '' };
    default:
      return state;
  }
}

const sortByPosition = (a, b) => (a.position || 0) - (b.position || 0);

// O-4509: the ManualTax forecast row. Derived here rather than persisted or
// baked into the props so it appears and disappears the moment the toggle is
// flipped — deriving it server-side meant a page reload to see either state.
// Skipped when the offer already carries a tax line of its own, since stacking
// the two is the double-count the feature exists to remove.

export function manualTaxForecastRow(offer, lineItems) {
  if (!offer || !offer.apply_tax_after_facility_fee) return null;

  const rate = parseFloat(offer.applicable_manual_tax_rate);
  if (!rate || rate <= 0) return null;

  const ownTaxLine = lineItems.some((li) => (
    li.category === 'BEFORE_ADJUSTED_DEDUCTIONS'
      && li.kind !== KIND_MANUAL_TAX_PROJECTION
      && looksLikeATaxLine(li)
  ));
  if (ownTaxLine) return null;

  return {
    id: null,
    uuid: 'manual-tax-forecast',
    name: 'Admission Tax',
    kind: KIND_MANUAL_TAX_PROJECTION,
    category: 'BEFORE_ADJUSTED_DEDUCTIONS',
    calc_type: 'percentage_of_gross',
    operator: 'of',
    left_hand: rate,
    right_hand: 0,
    left_hand_forecast: rate,
    right_hand_forecast: 0,
    show_in_offer: true,
    position: 9999,
    // There is no record behind this row, so an edit has nothing to save to and a
    // delete would PATCH /v3_budget_line_items/null. The flag is what every
    // locked-input, drag and row-menu branch already keys on.
    isAutoGenerated: true,
  };
}

function categorizeLineItems(lineItems) {
  return {
    ticketLineItems: lineItems.filter((li) => li.category === 'TICKET').sort(sortByPosition),
    additionLineItems: lineItems.filter((li) => li.category === 'ADDITION').sort(sortByPosition),
    incomeLineItems: lineItems.filter((li) => li.category === 'INCOME').sort(sortByPosition),
    expenseLineItems: lineItems.filter((li) => li.category === 'EXPENSES').sort(sortByPosition),
    beforeAdjustedDeductionsLineItems: lineItems.filter(
      (li) => li.category === 'BEFORE_ADJUSTED_DEDUCTIONS'
    ).sort(sortByPosition),
    deductionLineItems: lineItems.filter((li) => li.category === 'DEDUCTIONS').sort(sortByPosition),
  };
}

export const V3OfferProvider = ({ children, csrfToken, team, currentUser, initialOffer, initialBudget, initialLineItems, eventOffers, confirm, entryOrigin, changelogVersions, lastEditedAt, editorVariant, openFtuxModal }) => {
  const [state, dispatch] = useReducer(v3OfferReducer, {
    ...initialState,
    offer: initialOffer || null,
    budget: initialBudget || null,
    lineItems: initialLineItems || [],
    eventOffers: eventOffers || [],
  });

  // Re-derived on every render so flipping the toggle updates the table
  // immediately (O-4509).
  // Strip any previously-derived copy first: updating the budget flattens the
  // categorised lists back into lineItems, which would otherwise re-admit the
  // derived row and duplicate it on every edit.
  const persistedLineItems = state.lineItems.filter((li) => li.kind !== KIND_MANUAL_TAX_PROJECTION);
  const forecastRow = manualTaxForecastRow(state.offer, persistedLineItems);
  const categorizedLineItems = categorizeLineItems(
    forecastRow ? [...persistedLineItems, forecastRow] : persistedLineItems
  );

  const value = {
    csrfToken,
    team,
    currentUser,
    eventOffers: state.eventOffers,
    confirm,
    entryOrigin,
    changelogVersions,
    lastEditedAt,
    editorVariant,
    openFtuxModal,
    state,
    dispatch,
    ...categorizedLineItems,
    offer: state.offer,
    budget: state.budget,
    lineItems: state.lineItems,
    dealBrackets: state.dealBrackets,
    isSaving: state.isSaving,
    isSavingDealBracketBasis: state.isSavingDealBracketBasis,
    isLoading: state.isLoading,
    modalUIState: state.modalUIState,
    budgetTemplate: state.budgetTemplate,
    isCreatingTemplate: state.isCreatingTemplate,
    offerContacts: state.offerContacts,
    saveAsNewModalOpen: state.saveAsNewModalOpen,
    saveAsNewModalDraft: state.saveAsNewModalDraft,
  };

  return (
    <V3OfferContext.Provider value={value}>
      {children}
    </V3OfferContext.Provider>
  );
};

export default V3OfferContext;
